import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	InputAdornment,
	InputLabel,
	TextField,
} from '@mui/material'
import * as React from 'react'
import CloseIcon from '@mui/icons-material/Close'

import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { RequestBacklog, RequestWorker } from '../../types/Work/Request'
import { getQCs } from '../../services/requestService'
import { Employee } from '../../types/Employee/Employee'
import { reassignRequest } from '../../services/inspectionsService'
import { WorkItemTask } from '../../types/Work/WorkItemTask'
import { showErrorToast } from '../../services/CommonServices/alertServices'
type ReassignmentDialogProps = {
	loadParentData: () => Promise<void>
	request: RequestBacklog
	taskName: string
}

export const ReassignmentDialog = ({ loadParentData, request, taskName }: ReassignmentDialogProps) => {
	const [open, setOpen] = React.useState(false)
	const [qcWorkersOfProject, setQcWorkersOfProject] = React.useState<Employee[]>([])
	const [assignedToId, setAssignedToId] = React.useState<number>()
	const [assignAll, setAssignAll] = React.useState(false)

	const [submitting, setSubmitting] = React.useState<boolean>(false)

	React.useEffect(() => {
		if (open) {
			;(async () => {
				const res = await getQCs()
				res && setQcWorkersOfProject(res)
			})()
		}
        return () => {
            setAssignAll(false)
            setAssignedToId(undefined)
            setQcWorkersOfProject([])
        }
	}, [open])

	const handleClickOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		setOpen(true)
	}
	const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		setOpen(false)
	}

	async function handleSubmit(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
		event.stopPropagation()
		if (assignedToId) {
			setSubmitting(true)
			reassignRequest(request.id, assignedToId, assignAll)
				.then((res) => {
					setOpen(false)
					loadParentData()
				})
				.catch((ex) => {
					showErrorToast(ex)
				})
				.finally(() => {
					setSubmitting(false)
				})
		}
	}

	return (
		<>
			<Button
				onClick={handleClickOpen}
				title='Reassign'
				color='primary'
                sx={{p: 0, minWidth: '3rem'}}
                variant='contained'
			>
				<AssignmentIndIcon sx={{fontSize: '1.6rem'}}/>
			</Button>

			<Dialog open={open} onClose={handleClose} fullWidth >
				<div
					onClick={(e: React.MouseEvent<HTMLDivElement>) => {
						e.stopPropagation() // Prevent any event inside the dialog from bubbling up
					}}
				>
					<DialogTitle>Reassign {taskName} Inspection Request</DialogTitle>
					<IconButton
						onClick={handleClose}
						sx={(theme) => ({
							position: 'absolute',
							right: 8,
							top: 8,
							color: theme.palette.grey[500],
						})}
					>
						<CloseIcon />
					</IconButton>
					<DialogContent sx={{height: '20vh'}}>
						<Box sx={{ display: 'flex', flexDirection: 'column' }} gap={4}>
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
								<Autocomplete
                                    fullWidth
									disablePortal
									options={qcWorkersOfProject}
									getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
									isOptionEqualToValue={(option, value) => option.id === value.id}
									onChange={(_, newValue) => setAssignedToId(newValue ? newValue.id : undefined)}
									renderInput={(params) => (
										<TextField {...params} label='Assign To' variant='filled' fullWidth />
									)}
								/>
							</Box>
							{assignedToId && (
								<Box
									sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
									gap={2}
								>
									<FormControlLabel
										control={
											<Checkbox
												checked={assignAll}
												onChange={(e) => setAssignAll(e.target.checked)}
											/>
										}
										label={`Assign all this worker's pending requests to ${
											qcWorkersOfProject.find((w) => w.id === assignedToId)?.firstName
										} ${qcWorkersOfProject.find((w) => w.id === assignedToId)?.lastName}`}
									/>
								</Box>
							)}
						</Box>
					</DialogContent>
					<DialogActions sx={{ justifyContent: 'space-between' }}>
						<Button color='warning' variant='contained' onClick={handleClose}>
							Cancel
						</Button>
						{submitting ? (
							<CircularProgress color='success' />
						) : (
							<Button color='success' variant='contained' onClick={handleSubmit}>
								Submit
							</Button>
						)}
					</DialogActions>
				</div>
			</Dialog>
		</>
	)
}
